import { useState } from "react";
import AuthShell from "./AuthShell";
import AuthField from "./AuthField";
import Button from "../../ui/Button";
import ConsentCheckbox from "../../Legal/ConsentCheckbox";
import { LegalDocLink } from "../../Legal/LegalDocLink";
import { LEGAL_PUBLIC_ENABLED } from "../../../constants/legalPaths";
import { ageFromDob } from "../../../utils/clientAppUtils";
import a from "./auth.module.css";

const EMPTY_CHILD = { fullName: "", date_of_birth: "" };

function ModePicker({ onPick }) {
  return (
    <div className={a.regLinkModeList}>
      <button
        type="button"
        className={a.regLinkModeBtn}
        onClick={() => onPick("family")}
      >
        <span className={a.regLinkModeIcon} aria-hidden>
          👨‍👩‍👧
        </span>
        <span className={a.regLinkModeText}>
          <strong>Семья</strong>
          <small>Родитель и один или несколько детей</small>
        </span>
      </button>
      <button
        type="button"
        className={a.regLinkModeBtn}
        onClick={() => onPick("solo")}
      >
        <span className={a.regLinkModeIcon} aria-hidden>
          🏃
        </span>
        <span className={a.regLinkModeText}>
          <strong>Один человек</strong>
          <small>Спортсмен регистрируется сам</small>
        </span>
      </button>
    </div>
  );
}

export default function AuthRegisterByLinkFlow({
  clubName,
  linkInfo,
  backTo,
  backLabel,
  loading,
  error,
  onSubmit,
  footer,
  segment,
}) {
  const [regMode, setRegMode] = useState(null);
  const [step, setStep] = useState(1);
  const [parent, setParent] = useState({
    fullName: "",
    phone: "",
    email: "",
  });
  const [solo, setSolo] = useState({
    fullName: "",
    phone: "",
    email: "",
    date_of_birth: "",
  });
  const [children, setChildren] = useState([{ ...EMPTY_CHILD }]);
  const [consentPd, setConsentPd] = useState(false);

  const patchParent = (key, val) => setParent((p) => ({ ...p, [key]: val }));
  const patchSolo = (key, val) => setSolo((p) => ({ ...p, [key]: val }));
  const patchChild = (idx, patch) =>
    setChildren((list) =>
      list.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    );

  const groupHint = linkInfo?.group?.name
    ? `Группа: ${linkInfo.group.name}`
    : null;

  const pickMode = (mode) => {
    setRegMode(mode);
    setStep(1);
  };

  const step1Family = (e) => {
    e.preventDefault();
    if (!parent.fullName.trim() || !parent.email.trim()) return;
    setStep(2);
  };

  const finishFamily = (e) => {
    e.preventDefault();
    if (LEGAL_PUBLIC_ENABLED && !consentPd) return;
    const childRows = children
      .map((c) => ({
        fullName: c.fullName.trim(),
        date_of_birth: c.date_of_birth || null,
      }))
      .filter((c) => c.fullName);
    if (!childRows.length || childRows.some((c) => !c.date_of_birth)) return;
    onSubmit({
      registrationType: "family",
      fullName: parent.fullName.trim(),
      phone: parent.phone.trim() || undefined,
      email: parent.email.trim().toLowerCase(),
      children: childRows,
      ...(LEGAL_PUBLIC_ENABLED ? { consent_pd: true } : {}),
    });
  };

  const finishSolo = (e) => {
    e.preventDefault();
    if (LEGAL_PUBLIC_ENABLED && !consentPd) return;
    if (!solo.fullName.trim() || !solo.email.trim() || !solo.date_of_birth) return;
    onSubmit({
      registrationType: "solo",
      fullName: solo.fullName.trim(),
      phone: solo.phone.trim() || undefined,
      email: solo.email.trim().toLowerCase(),
      dateOfBirth: solo.date_of_birth,
      ...(LEGAL_PUBLIC_ENABLED ? { consent_pd: true } : {}),
    });
  };

  const step1Valid = parent.fullName.trim() && parent.email.trim();
  const step2Valid = children.some(
    (c) => c.fullName.trim() && c.date_of_birth,
  );
  const soloValid =
    solo.fullName.trim() && solo.email.trim() && solo.date_of_birth;

  const soloAge = ageFromDob(solo.date_of_birth);

  let subtitle =
    "Выберите тип заявки — пароль зададите после подтверждения тренером.";
  if (regMode === "family") {
    subtitle = groupHint
      ? `${groupHint}. Укажите email родителя и данные детей. Если родитель уже в клубе — новый ребёнок добавится к его аккаунту в эту группу.`
      : "Укажите email родителя и данные детей. Если родитель уже в клубе — дети добавятся к существующему аккаунту.";
  } else if (regMode === "solo") {
    subtitle = groupHint
      ? `${groupHint}. Если вы уже в клубе — укажите тот же email, и вас добавят в эту группу.`
      : "Укажите email, дату рождения и при желании телефон.";
  }

  const stepCurrent = !regMode ? 1 : regMode === "family" ? step : 1;
  const stepTotal = !regMode ? 1 : regMode === "family" ? 2 : 1;
  const stepLabels =
    !regMode || regMode === "solo"
      ? ["Заявка"]
      : ["Родитель", "Дети"];

  return (
    <AuthShell
      modern
      wide
      backTo={backTo}
      backLabel={backLabel}
      showSwitcher={false}
      cardTitle={linkInfo?.title || "Заявка в клуб"}
      hint={subtitle}
      clubTag={clubName}
      stepCurrent={stepCurrent}
      stepTotal={stepTotal}
      stepLabels={stepLabels}
      segment={segment}
      footer={
        regMode === "family" && step === 2
          ? footer
          : regMode === "solo"
            ? footer
            : null
      }
    >
      {error ? <div className={a.errorBox}>{error}</div> : null}

      {!regMode ? (
        <ModePicker onPick={pickMode} />
      ) : regMode === "solo" ? (
        <form onSubmit={finishSolo}>
          <button
            type="button"
            className={a.regLinkModeBack}
            onClick={() => setRegMode(null)}
          >
            ← Другой тип заявки
          </button>
          <AuthField
            label="ФИО"
            value={solo.fullName}
            onChange={(e) => patchSolo("fullName", e.target.value)}
            required
            autoFocus
          />
          <AuthField
            label="Email"
            type="email"
            value={solo.email}
            onChange={(e) => patchSolo("email", e.target.value)}
            autoComplete="email"
            required
            hint="На этот адрес придёт ссылка для входа после подтверждения"
          />
          <AuthField
            label="Телефон"
            asPhone
            value={solo.phone}
            onChange={(v) => patchSolo("phone", v)}
            autoComplete="tel"
            hint="Необязательно"
          />
          <AuthField
            label="Дата рождения"
            asBirthDate
            value={solo.date_of_birth}
            onChange={(v) => patchSolo("date_of_birth", v)}
            required
            max={new Date().toISOString().slice(0, 10)}
            hint={soloAge != null ? `Возраст: ${soloAge} лет` : undefined}
          />
          {LEGAL_PUBLIC_ENABLED ? (
            <ConsentCheckbox
              id="client-consent-solo"
              checked={consentPd}
              onChange={setConsentPd}
              disabled={loading}
            >
              Даю согласие на обработку персональных данных клубом согласно{" "}
              <LegalDocLink doc="clientConsent">тексту согласия</LegalDocLink>
              . Политика платформы:{" "}
              <LegalDocLink doc="privacy">конфиденциальность</LegalDocLink>{" "}
              Бай.Спорт
            </ConsentCheckbox>
          ) : null}
          <Button
            type="submit"
            block
            size="lg"
            disabled={
              loading || !soloValid || (LEGAL_PUBLIC_ENABLED && !consentPd)
            }
          >
            {loading ? "Отправка…" : "Отправить заявку"}
          </Button>
        </form>
      ) : step === 1 ? (
        <form onSubmit={step1Family}>
          <button
            type="button"
            className={a.regLinkModeBack}
            onClick={() => setRegMode(null)}
          >
            ← Другой тип заявки
          </button>
          <AuthField
            label="ФИО родителя"
            value={parent.fullName}
            onChange={(e) => patchParent("fullName", e.target.value)}
            required
            autoFocus
          />
          <AuthField
            label="Email родителя"
            type="email"
            value={parent.email}
            onChange={(e) => patchParent("email", e.target.value)}
            autoComplete="email"
            required
            hint="На этот адрес придёт ссылка для входа после подтверждения"
          />
          <AuthField
            label="Телефон родителя"
            asPhone
            value={parent.phone}
            onChange={(v) => patchParent("phone", v)}
            autoComplete="tel"
            hint="Необязательно"
          />
          <Button type="submit" block size="lg" disabled={!step1Valid}>
            Далее
          </Button>
        </form>
      ) : (
        <form onSubmit={finishFamily}>
          {children.map((child, idx) => {
            const age = ageFromDob(child.date_of_birth);
            return (
              <div key={idx}>
                <AuthField
                  label={idx === 0 ? "ФИО ребёнка" : `ФИО ребёнка ${idx + 1}`}
                  value={child.fullName}
                  onChange={(e) => patchChild(idx, { fullName: e.target.value })}
                  required={idx === 0}
                />
                <AuthField
                  label="Дата рождения"
                  asBirthDate
                  value={child.date_of_birth}
                  onChange={(v) => patchChild(idx, { date_of_birth: v })}
                  required={idx === 0}
                  max={new Date().toISOString().slice(0, 10)}
                  hint={age != null ? `Возраст: ${age} лет` : undefined}
                />
              </div>
            );
          })}
          <button
            type="button"
            className={a.footerLinksModern}
            onClick={() => setChildren((list) => [...list, { ...EMPTY_CHILD }])}
          >
            + Добавить ещё ребёнка
          </button>
          {LEGAL_PUBLIC_ENABLED ? (
            <ConsentCheckbox
              id="client-consent-family"
              checked={consentPd}
              onChange={setConsentPd}
              disabled={loading}
            >
              Как законный представитель даю согласие на обработку персональных
              данных детей и своих данных клубом согласно{" "}
              <LegalDocLink doc="clientConsent">тексту согласия</LegalDocLink>
              . Политика платформы:{" "}
              <LegalDocLink doc="privacy">конфиденциальность</LegalDocLink>{" "}
              Бай.Спорт
            </ConsentCheckbox>
          ) : null}
          <div className={a.actions}>
            <Button
              type="submit"
              block
              size="lg"
              disabled={
                loading || !step2Valid || (LEGAL_PUBLIC_ENABLED && !consentPd)
              }
            >
              {loading ? "Отправка…" : "Отправить заявку"}
            </Button>
            <Button type="button" variant="ghost" block onClick={() => setStep(1)}>
              Назад
            </Button>
          </div>
        </form>
      )}
    </AuthShell>
  );
}
